import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SeoMeta from "../../components/SeoMeta";
import ProjectTitle from "./components/ProjectTitle";
import PortfolioBody from "./components/PortfolioBody";
import PortfolioStructuredData from "./components/PortfolioStructuredData";
import { useLoading } from "../../contexts/LoadingContext";

// Get locale from cookie
const getLocale = () => {
  const match = document.cookie.match(/(^|;)\s*locale=([^;]*)/);
  return match ? decodeURIComponent(match[2]) : "en";
};

// Convert Abstract block array into plain text for SEO meta tag
const extractAbstractText = (abstract) => {
  if (!Array.isArray(abstract)) return "";
  return abstract
    .filter((block) => block?.type === "paragraph" && Array.isArray(block.children))
    .map((block) =>
      block.children
        .map((child) => (typeof child?.text === "string" ? child.text : ""))
        .join("")
    )
    .join(" ")
    .trim();
};

function Project() {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [error, setError] = useState(null);
  const { setIsLoading } = useLoading();

  const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || "http://localhost:1337";
  const STRAPI_TOKEN = process.env.REACT_APP_STRAPI_TOKEN;
  const locale = getLocale();

  useEffect(() => {
    const fetchData = async () => {
      if (!STRAPI_URL || !STRAPI_TOKEN) {
        setError("Strapi URL or token is missing.");
        return;
      }

      const path = `/api/portfolios?filters[slug][$eq]=${slug}&locale=${locale}&populate[PortfolioContent][populate]=*&populate[seo][populate]=*`;
      const url = new URL(STRAPI_URL + path);

      setIsLoading(true);
      try {
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${STRAPI_TOKEN}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Fetch failed: ${response.status} - ${text}`);
        }

        const result = await response.json();
        setPageData(result.data?.[0] || null);
      } catch (err) {
        console.error("❌ Fetch error:", err.message);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [slug, STRAPI_URL, STRAPI_TOKEN, locale, setIsLoading]);

  if (error) {
    return <p className="text-danger text-center py-5">{error}</p>;
  }

  if (!pageData) {
    return <div className="text-center py-5">Loading...</div>;
  }

  const seo = pageData.seo || {};
  const abstractText = extractAbstractText(pageData.Abstract);
  const seoImage = seo.image?.formats?.large?.url || seo.image?.url || null;

  return (
    <>
      <SeoMeta
        lang={locale}
        title={seo.Title || pageData.title}
        description={seo.Description || abstractText}
        keywords={seo.keywords || ""}
        robots={seo.robots || "index, follow"}
        imageUrl={seoImage}
      />

      <PortfolioStructuredData
        title={pageData.title}
        abstract={pageData.Abstract}
        createdAt={pageData.createdAt}
        publishedAt={pageData.publishedAt}
        place={pageData.place}
        country={pageData.Country}
        images={pageData.PortfolioContent}
      />

      <section className="py-15 py-xl-20">
        <ProjectTitle data={pageData} />

      <PortfolioBody portfolioContent={pageData.PortfolioContent} />
      </section>
    </>
  );
}

export default Project;
